<template>
  <div>
    <div
      class="relative"
      :style="{
        height: diameter + 'px',
        width: diameter + 'px',
      }"
    >
      <div
        v-if="icon"
        class="absolute top-0 left-1/2 -translate-x-1/2 z-10 flex items-center justify-center"
        :style="{
          height: thickness + 'px',
          width: thickness + 'px',
        }"
      >
        <BaseIcon :size="iconSize" :class="iconColorClass">{{ icon }}</BaseIcon>
      </div>

      <div
        class="h-full w-full absolute left-0 top-0 duration-800 ease-linear"
        :style="{ transform: 'rotate(' + dotRotation + 'deg)' }"
      >
        <div
          class="absolute top-0 left-1/2 -translate-x-1/2 rounded-full"
          :class="dotColorClass"
          :style="{
            height: thickness + 'px',
            width: thickness + 'px',
            boxShadow: '3px 0px 3px rgba(0, 0, 0, 0.24)',
          }"
        ></div>
      </div>
      <svg
        :width="diameter"
        :height="diameter"
        class="block z-0"
        :style="{ width: diameter + 'px', height: diameter + 'px' }"
      >
        <circle
          class="transition-stroke-dashoffset"
          :class="trackStrokeColorClass"
          :cx="radius"
          :cy="radius"
          :r="adjustedRadius"
          :stroke-width="thickness"
          fill="transparent"
        />
        <circle
          class="transition-stroke-dashoffset ease-linear"
          :style="{ transitionDuration: duration }"
          :class="indicatorStrokeColorClass"
          :cx="radius"
          :cy="radius"
          :r="adjustedRadius"
          :stroke-width="thickness"
          fill="transparent"
          :stroke-dasharray="strokeDashArray"
          :stroke-dashoffset="strokeDashoffset"
          stroke-linecap="round"
          transform="rotate(-90)"
          :transform-origin="`${radius} ${radius}`"
        />
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseIcon from '../icon/BaseIcon.vue'

interface Props {
  percentage: number
  radius?: number
  thickness?: number
  disabled?: boolean
  color?: 'primary' | 'secondary' | 'tertiary' | 'neutral'
  icon?: string
}

const {
  disabled,
  percentage,
  radius = 12,
  thickness = 8,
  color = 'secondary',
} = defineProps<Props>()

const trackStrokeColorClass = computed(() => {
  if (color == 'neutral' || disabled) {
    return 'stroke-outline-variant'
  }
  if (color == 'primary') {
    return 'stroke-primary-container'
  }
  if (color == 'tertiary') {
    return 'stroke-tertiary-container'
  }
  if (color == 'secondary') {
    return 'stroke-secondary-container'
  }
})

const iconColorClass = computed(() => {
  if (color == 'neutral' || disabled) {
    return 'text-inverse-on-surface'
  }
  if (color == 'primary') {
    return 'text-on-primary'
  }
  if (color == 'tertiary') {
    return 'text-on-tertiary'
  }
  if (color == 'secondary') {
    return 'text-on-secondary'
  }
})

const indicatorStrokeColorClass = computed(() => {
  if (color == 'neutral' || disabled) {
    return 'stroke-on-surface-variant'
  }
  if (color == 'primary') {
    return 'stroke-primary'
  }
  if (color == 'tertiary') {
    return 'stroke-tertiary'
  }
  if (color == 'secondary') {
    return 'stroke-secondary'
  }
})

const dotColorClass = computed(() => {
  if (color == 'neutral' || disabled) {
    return 'bg-on-surface-variant'
  }
  if (color == 'primary') {
    return 'bg-primary'
  }
  if (color == 'tertiary') {
    return 'bg-tertiary'
  }
  if (color == 'secondary') {
    return 'bg-secondary'
  }
})

const iconSize = computed(() => {
  if (thickness < 24) {
    return '16'
  }
  if (thickness < 40) {
    return '18'
  }
  if (thickness >= 40) {
    return '40'
  }
  return '24'
})

const adjustedRadius = computed(() => radius - thickness / 2)
const duration = (percentage > 100 ? (1 / (percentage / 100)) * 800 : 800) + 'ms'

const diameter = computed(() => radius * 2)
const circumference = computed(() => 2 * Math.PI * adjustedRadius.value)
const strokeDashArray = computed(() => circumference.value)

const strokeDashoffset = ref(circumference.value)

const updateProgress = () => {
  strokeDashoffset.value = Math.max((1 - percentage / 100) * circumference.value, 0)
  dotRotation.value = 360 * (percentage / 100)
}
const dotRotation = ref(0)

watch(
  () => percentage,
  () => {
    setTimeout(() => {
      updateProgress()
    }, 10)
  },
  { immediate: true },
)
</script>
